import { useComponent } from "@/hooks/useMicrositeComponent";
import { cn } from "@/lib/utils";
import { IMicrositeComponent, MComponentMediaProps } from "@/types/microsite";
import {
  canvaRule,
  gdocsRule,
  instagramRule,
  klipIdRule,
  soundcloudRule,
  spotifyRule,
  tiktokRule,
  twitterRule,
  xRule,
  youtubePlaylist,
  youtubeRule,
  youtubeShorts,
} from "@/vars/media-rule";
import { ArrowUpRightIcon } from "@heroicons/react/24/solid";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { InstagramEmbed, TikTokEmbed, XEmbed } from "react-social-media-embed";
import { useDebounce } from "react-use";

import { SpoilerButton } from "./Spoiler";

const MediaComponent = {
  kind: "MEDIA",
  Render: ({
    microsite,
    isBuilder,
    id: _id,
    isFocus,
    data: {
      autoPlay: _autoPlay,
      isSpoiler,
      spoilerButtonFont,
      spoilerButtonColor,
      spoilerButtonIcon,
      spoilerButtonText,
      spoilerButtonTextPosition,
      needPassword,
      passwordMessage,
      url: _url,
    },
  }) => {
    const [showReRender, setShowReRender] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [url, setUrl] = useState(_url);
    const { focusTo } = useComponent();

    const autoPlay = isBuilder ? false : _autoPlay;

    const { isValidMedia, id, kind } = useMemo(() => {
      let isValidMedia = false;
      let id = "";
      let kind = "";

      if (youtubeRule.test(url)) {
        kind = "youtube";
        const match = url.match(youtubeRule);
        id = match ? match[2] || match[3] || match[4] : "";
        isValidMedia = true;
      } else if (youtubeShorts.test(url)) {
        kind = "youtube_shorts";
        const match = url.match(youtubeShorts);
        id = match ? match[1] : "";
        isValidMedia = true;
      } else if (youtubePlaylist.test(url)) {
        kind = "youtube_playlist";
        const u = new URL(url);
        id = u.searchParams.get("list") as string;
        isValidMedia = true;
      } else if (instagramRule.test(url)) {
        kind = "instagram";
        isValidMedia = true;
      } else if (twitterRule.test(url)) {
        kind = "twitter";
        isValidMedia = true;
      } else if (xRule.test(url)) {
        kind = "twitter";
        isValidMedia = true;
      } else if (tiktokRule.test(url)) {
        kind = "tiktok";
        isValidMedia = true;
      } else if (soundcloudRule.test(url)) {
        kind = "soundcloud";
        isValidMedia = true;
      } else if (klipIdRule.test(url)) {
        kind = "klipid";
        isValidMedia = true;
        const match = url.match(klipIdRule);
        id = match ? match[1] : "";
      } else if (spotifyRule.test(url)) {
        kind = "spotify";
        const match = url.match(spotifyRule);
        id = match ? match[1] + "/" + match[2] : "";
        isValidMedia = true;
      } else if (canvaRule.test(url)) {
        kind = "canva";
        const match = url.match(canvaRule);
        id = match ? match[1] : "";
        isValidMedia = true;
      } else if (gdocsRule.test(url)) {
        kind = "gdocs";
        const match = url.match(gdocsRule);
        id = match ? url : "";
        isValidMedia = true;
      }

      return { isValidMedia, id, kind };
    }, [url]);

    const onProtectedOpen = (data) => {
      setUrl(data.url);
    };

    const prevInteract = useRef<any>(undefined);
    useEffect(() => {
      if (!prevInteract.current && microsite?._hasInteract) {
        setShowReRender(false);
        setTimeout(() => {
          setShowReRender(true);
        }, 50);
      }
      prevInteract.current = microsite?._hasInteract;
    }, [microsite?._hasInteract]);

    const [, stop] = useDebounce(
      () => {
        if (isBuilder) {
          setUrl(_url);
        }
      },
      500,
      [_url, isBuilder]
    );

    useEffect(() => {
      stop();
      return () => {
        stop();
      };
    }, []);

    if (!showReRender) return <Fragment />;

    if (isValidMedia || needPassword) {
      return (
        <SpoilerButton
          spoilerButtonFont={spoilerButtonFont}
          microsite={microsite}
          spoilerButtonColor={spoilerButtonColor}
          spoilerButtonIcon={spoilerButtonIcon}
          spoilerButtonText={spoilerButtonText}
          spoilerButtonTextPosition={spoilerButtonTextPosition}
          isOpen={isOpen}
          onToggle={() => setIsOpen(!isOpen)}
          isBuilder={isBuilder}
          isSpoiler={isSpoiler}
          needPassword={needPassword}
          onProtectedOpen={onProtectedOpen}
          passwordMessage={passwordMessage}
          id={_id}
          isFocus={isFocus}
        >
          <VideoComponentStyle
            id={isBuilder && !isSpoiler ? `mcomp-${_id}` : undefined}
            isSpoiler={isSpoiler}
            isOpen={isOpen}
          >
            {isBuilder && (
              <div
                className="absolute top-0 left-0 z-10 w-full h-full"
                onClick={() => {
                  focusTo(_id as string);
                }}
              />
            )}
            {kind === "twitter" && <XEmbed url={url} width={"100%"} />}
            {kind === "tiktok" && <TikTokEmbed url={url} width={"100%"} />}
            {kind === "soundcloud" && (
              <SoundcloudEmbed url={url} autoPlay={autoPlay} />
            )}
            {kind === "instagram" && (
              <InstagramEmbed url={url} width={"100%"} />
            )}
            {kind === "youtube" && (
              <div className="relative bg-black bg-opacity/50 pb-[55.9%] rounded-md overflow-hidden">
                <iframe
                  width={"100%"}
                  height={"100%"}
                  className="absolute"
                  src={
                    "https://www.youtube.com/embed/" +
                    id +
                    (autoPlay ? "?autoplay=1" : "")
                  }
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            {kind === "youtube_shorts" && (
              <div className="relative bg-black bg-opacity/50 pb-[177.78%] rounded-md overflow-hidden">
                <iframe
                  width={"100%"}
                  height={"100%"}
                  className="absolute"
                  src={
                    "https://www.youtube.com/embed/" +
                    id +
                    (autoPlay ? "?autoplay=1" : "")
                  }
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            {kind === "youtube_playlist" && (
              <div className="relative bg-black bg-opacity/50 pb-[55.9%] rounded-md overflow-hidden">
                <iframe
                  width={"100%"}
                  height={"100%"}
                  className="absolute"
                  src={
                    "https://www.youtube.com/embed/videoseries?list=" +
                    id +
                    (autoPlay ? "?autoplay=1" : "")
                  }
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            {kind === "spotify" && <SpotifyEmbed id={id} />}
            {kind === "klipid" && <KlipIDEmbed id={id} key={id} />}
            {kind === "canva" && <CanvaEmbed id={id} key={id} />}
            {kind === "gdocs" && <GdocsEmbed id={id} key={id} />}
          </VideoComponentStyle>
        </SpoilerButton>
      );
    }

    return <Fragment />;
  },
} as IMicrositeComponent<MComponentMediaProps>;

const VideoComponentStyle = ({ id, children, isSpoiler, isOpen }) => {
  if (isSpoiler === true && !isOpen) {
    return <div />;
  }

  return (
    <div id={id} className="relative p-2 w-full">
      {children}
    </div>
  );
};

const SoundcloudEmbed = ({ url, autoPlay = false }) => {
  const finalUrl = useMemo(() => {
    const u = new URL("https://w.soundcloud.com/player/");
    u.searchParams.set("url", url);
    u.searchParams.set("color", "#d27244");
    u.searchParams.set("auto_play", autoPlay ? "true" : "false");
    u.searchParams.set("hide_related", "false");
    u.searchParams.set("show_comments", "true");
    u.searchParams.set("show_user", "true");
    u.searchParams.set("show_reposts", "false");
    u.searchParams.set("show_teaser", "true");
    u.searchParams.set("visual", "true");
    return u.toString();
  }, [url, autoPlay]);

  return (
    <iframe
      src={finalUrl}
      className="overflow-hidden w-full h-60 rounded-lg"
      allowFullScreen
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
    />
  );
};

const SpotifyEmbed = ({ id }) => {
  const { finalUrl, type } = useMemo(() => {
    const u = new URL("https://open.spotify.com/embed/" + id);
    u.searchParams.set("utm_source", "generator");
    return { finalUrl: u.toString(), type: id.split("/")[0] };
  }, [id]);

  return (
    <iframe
      src={finalUrl}
      className={cn("rounded-lg w-full overflow-hidden", {
        "!h-24": type === "track",
      })}
      style={{ height: 352 }}
      allowFullScreen
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      loading="lazy"
    />
  );
};

const KlipIDEmbed = ({ id }) => {
  useEffect(() => {
    const embed = document.createElement("script");
    embed.setAttribute("async", "");
    embed.setAttribute("src", "https://klip.id/embed.js");
    document.body.appendChild(embed);
    return () => {
      if (embed) {
        document.body.removeChild(embed);
      }
    };
  }, []);

  useEffect(() => {
    let timeouter;
    if (typeof window !== "undefined") {
      timeouter = setInterval(() => {
        //@ts-ignore
        if (window._embedKlip) {
          //@ts-ignore
          window._embedKlip();
          clearInterval(timeouter);
        }
      }, 100);
    }
    return () => {
      clearTimeout(timeouter);
    };
  }, []);

  return (
    <Fragment>
      <blockquote className="klip-id-embed" cite={id}></blockquote>
    </Fragment>
  );
};

const CanvaEmbed = ({ id }) => {
  return (
    <div className="bg-white rounded-lg border">
      <iframe
        loading="lazy"
        src={`https://canva.com/design/${id}/view?embed`}
        allowFullScreen
        allow="fullscreen"
        className="bg-white rounded-lg w-full h-[280px] overflow-hidden"
      />
      <footer className="flex justify-end p-1">
        <a
          target="_blank"
          href={`https://canva.com/design/${id}/view`}
          className="flex items-center text-sm underline"
        >
          Open Design <ArrowUpRightIcon className="ml-1 w-3" />
        </a>
      </footer>
    </div>
  );
};

const GdocsEmbed = ({ id }) => {
  const _url = useMemo(() => {
    const url = new URL(id);

    const [, type] = url.pathname.split("/");

    // console.log("type", type);

    if (type === "document") {
      return `https://docs.google.com/${url.pathname}?embedded=true`;
    }
    return id;
  }, [id]);

  return (
    <div className="bg-white rounded-lg border">
      <iframe
        loading="lazy"
        src={_url}
        allowFullScreen
        allow="fullscreen"
        className="bg-white rounded-lg w-full h-[600px] overflow-hidden"
      />
    </div>
  );
};

export default MediaComponent;
